// Cave84 — Firebase SDK initialization
// Singletons for the entire app. Import { auth, db, storage } from here.
// Project: cave84-4ee22 (europe-west1, Blaze plan)

import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { getAuth, GoogleAuthProvider, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Fail fast on misconfiguration. The app cannot do anything useful without Firebase.
const required: (keyof FirebaseOptions)[] = ['apiKey', 'authDomain', 'projectId', 'appId'];
const missing = required.filter((k) => !config[k]);
if (missing.length > 0) {
  throw new Error(
    `Cave84: missing Firebase config keys: ${missing.join(', ')}. ` +
      `Check apps/web/.env.local (or Vercel env). All keys must start with VITE_.`
  );
}

export const firebaseApp = initializeApp(config);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Persist auth across reloads. We rely on this so the PWA "feels" logged in
// after closing/reopening; without it, every fresh tab requires re-auth.
void setPersistence(auth, browserLocalPersistence);

export const OWNER_EMAIL = import.meta.env.VITE_OWNER_EMAIL;
if (!OWNER_EMAIL) {
  console.warn(
    'Cave84: VITE_OWNER_EMAIL is not set. First-login bootstrap will treat all users as viewers.'
  );
}
